// Cierre de la venta: códigos aplicados y bonos con su estado. Puro.
import { shownDiscountCode } from '@/shared/patterns/SalePromotion/discountCode';
import type { ChargeLine } from './saleCharge';
import {
    promotionBonusDisplayStatus,
    promotionBonusText,
    type PromotionBonusDisplayStatus,
} from './promotionBonusDisplay';
import type { WizardSession } from './types';

export interface AppliedCode {
    key: string;
    code: string;
    /** Lo que bajó el precio; `null` si la línea no lo trae. */
    discountCents: number | null;
    lineName: string;
}

/** Un código por línea donde se aplicó; los nombres salen de las líneas de cobro. */
export function appliedCodesOf(
    session: WizardSession,
    lines: readonly ChargeLine[],
): AppliedCode[] {
    const nameById = new Map(lines.map((l) => [l.lineId, l.name]));
    return (session.reservedLines ?? [])
        .filter((l) => l.status === 'RESERVED')
        .flatMap((l) => {
            const code = shownDiscountCode(l);
            if (code === undefined) return [];
            return [
                {
                    key: l.lineId,
                    code,
                    discountCents: l.discountCents ?? null,
                    lineName: nameById.get(l.lineId) ?? l.sku,
                },
            ];
        });
}

export type ClosingBonusKind = 'WELCOME' | 'PROMO';

export interface ClosingBonus {
    key: string;
    kind: ClosingBonusKind;
    label: string;
    amountCents: number;
    /** `PAID` se celebra con el monto; los otros dos solo informan. */
    displayStatus: PromotionBonusDisplayStatus;
}

/**
 * Bonos de la venta, uno por elemento: el de bienvenida con la recarga aplicada y monto conocido, y los
 * de promoción con su estado para mostrar.
 */
export function closingBonusesOf(
    session: Pick<WizardSession, 'welcome'>,
    welcomeAmountCents: number | undefined,
    promoBonuses: readonly {
        label: string;
        amountCents: number;
        displayStatus: PromotionBonusDisplayStatus;
    }[] = [],
): ClosingBonus[] {
    const welcomeApplied = session.welcome.welcomeAccredited || session.welcome.topupApplied;
    const welcome: ClosingBonus[] =
        welcomeApplied && welcomeAmountCents !== undefined
            ? [
                  {
                      key: 'welcome',
                      kind: 'WELCOME',
                      label: 'Bono de bienvenida',
                      amountCents: welcomeAmountCents,
                      displayStatus: 'PAID',
                  },
              ]
            : [];
    const promos = promoBonuses.map((b, i) => ({
        key: `promo-${String(i)}`,
        kind: 'PROMO' as const,
        label: b.label,
        amountCents: b.amountCents,
        displayStatus: b.displayStatus,
    }));
    return [...welcome, ...promos];
}

/** El bono de la promo de crédito como línea del cierre; el de venta lo pinta el cupón de Promociones. */
export function creditPromotionBonusesOf(
    session: Pick<WizardSession, 'creditPromotionBonus'>,
): { label: string; amountCents: number; displayStatus: PromotionBonusDisplayStatus }[] {
    const bonus = session.creditPromotionBonus;
    if (!bonus) return [];
    const displayStatus = promotionBonusDisplayStatus({ kind: 'CREDIT', status: bonus.status });
    if (displayStatus === null) return [];
    return [
        {
            label: promotionBonusText(displayStatus, bonus.amountCents),
            amountCents: bonus.amountCents,
            displayStatus,
        },
    ];
}

/** El resumen consulta el enrolamiento solo con un crédito cuyo bono sigue RESERVED. */
export function creditPromotionBonusRetrying(
    isCredit: boolean,
    session: Pick<WizardSession, 'creditPromotionBonus'> | undefined,
): boolean {
    return isCredit && session?.creditPromotionBonus?.status === 'RESERVED';
}
